import { Injectable } from '@angular/core';
import { DefaultUrlSerializer, Routes, UrlSegment, UrlTree } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CheckoutGuard } from './services/checkout/checkout.guard';

export function ids(url: UrlSegment[]) {
  if (url.length === 1 && url[0].path.match(/^.*-\d+$|\d+$/)) {
    return { consumed: url };
  }

  return undefined;
}

@Injectable()
export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
  override parse(url: string): UrlTree {
    const [route, ...queryParams] = url.split('?');
    let parsedUrl = route.toLowerCase();
    if (queryParams.length > 0) {
      const params = queryParams.join('?').split('&');
      const parsedParams: string[] = [];
      for (const param of params) {
        const [key, ...values] = param.split('=');
        const value = `${key.toLowerCase()}=${values.join('=').replaceAll('+', '%2B')}`;
        parsedParams.push(value);
      }
      parsedUrl = `${parsedUrl}?${parsedParams.join('&')}`;
    }
    return super.parse(parsedUrl);
  }
}

export const routes: Routes = [
  {
    path: environment.urls.products,
    children: [
      {
        matcher: ids,
        loadComponent: () => import('./pages/products/product-detail/product-detail.component').then((m) => m.ProductDetailComponent),
      },
      {
        path: '**',
        loadComponent: () => import('./pages/products').then((m) => m.ProductOverviewWrapperComponent),
        data: { showFilter: true },
      },
    ],
  },
  {
    path: '',
    children: [
      { path: '', loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent) },
      {
        path: environment.urls.content.trade_conditions,
        loadComponent: () => import('./pages/content-pages').then((m) => m.TradeConditionsComponent),
      },
      { path: environment.urls.content.gdpr, loadComponent: () => import('./pages/content-pages').then((m) => m.GDPRComponent) },
      {
        path: environment.urls.content.cookie_policy,
        loadComponent: () => import('./pages/content-pages').then((m) => m.CookiePolicyComponent),
      },
      {
        path: environment.urls.content.questions_and_answers,
        loadComponent: () =>
          import('./pages/content-pages/questions-and-answers/questions-and-answers.component').then((m) => m.QuestionsAndAnswersComponent),
      },
      {
        path: environment.urls.content.newsletter_signup,
        loadComponent: () => import('./pages/content-pages/news/newsletter-signup.component').then((m) => m.NewsletterSignupComponent),
      },
      {
        path: environment.urls.content.contact_us,
        loadComponent: () => import('./pages/content-pages/contact-us/contact-us.component').then((m) => m.ContactUsComponent),
      },
      {
        path: environment.urls.content.customer_service,
        loadComponent: () =>
          import('./pages/content-pages/customer-service/customer-service.component').then((m) => m.CustomerServiceComponent),
      },
      {
        path: environment.urls.content.merchant_content,
        children: [{ path: '**', loadComponent: () => import('./pages/content-pages').then((m) => m.SimpleContentComponent) }],
      },
    ],
  },
  {
    path: environment.urls.gift_card,
    loadComponent: () => import('./pages/products').then((m) => m.GiftCardComponent),
  },
  {
    path: environment.urls.basket,
    loadComponent: () =>
      import('./pages/basket/overview/basket-overview-wrapper/basket-overview-wrapper.component').then(
        (m) => m.BasketOverviewWrapperComponent,
      ),
    data: { showBasket: false },
  },
  {
    path: environment.urls.checkout,
    children: [{ path: '', loadComponent: () => import('./pages/basket/checkout/checkout.component').then((m) => m.CheckoutComponent) }],
    canActivateChild: [CheckoutGuard],
    data: { showBasket: false, showShipping: false, showNavigation: false },
  },
  {
    path: `${environment.urls.confirmation}`,
    loadComponent: () => import('./pages/finalize-order/success/success.component').then((m) => m.SuccessComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  {
    path: `${environment.urls.confirmation}/:id`,
    loadComponent: () => import('./pages/finalize-order/success/success.component').then((m) => m.SuccessComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  {
    path: environment.urls.kiosk.confirmation,
    loadComponent: () => import('./pages/finalize-order/kiosk-success/kiosk-success.component').then((m) => m.KioskSuccessComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  {
    path: environment.urls.kiosk.closed,
    loadComponent: () => import('./pages/kiosk-closed/kiosk-closed.component').then((m) => m.KioskClosedComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  {
    path: environment.urls.member_set_password,
    loadComponent: () => import('./pages/member').then((m) => m.MemberPasswordComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  {
    path: environment.urls.page_not_found,
    loadComponent: () => import('./pages/404/page-not-found.component').then((m) => m.PageNotFoundComponent),
    data: { showBasket: false, showShipping: false, showNavigation: false, showFooter: false },
  },
  { path: '**', redirectTo: environment.urls.page_not_found },
];
